"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { getAssetUrl, type ImageVariant } from "@/utils/assetUrl";
import { useDarkColors } from "@/hooks/useDarkColors";

// 피드 이미지 — 작성 직후 깨짐(?) 방지용.
// 서버는 업로드 시 원본만 즉시 저장하고 _medium/_large webp 변형은 워커가 비동기 생성한다.
// 작성 직후 변형(medium/large)을 요청하면 아직 없어서 404 → 깨진 이미지 아이콘이 뜬다.
// (탭 전환 후 재마운트하면 그때는 변형이 생성돼 정상 로드되던 기존 증상)
// → 변형 로드 실패 시 잠깐 재시도(생성 대기) 후 즉시 존재하는 원본으로 폴백.
//   로딩/실패 중에는 깨진 아이콘 대신 placeholder 배경만 보여준다.

const RETRY_DELAYS_MS = [800, 1600, 3000]; // 변형 생성 대기 — 점증 백오프

export default function FeedImage({
  path,
  variant,
  maxHeight,
}: {
  path: string;
  variant: ImageVariant;
  maxHeight: number;
}) {
  const c = useDarkColors();
  const [src, setSrc] = useState(() => getAssetUrl(path, variant));
  const [loaded, setLoaded] = useState(false);
  const retryRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // path/variant 변경 시 초기화
  useEffect(() => {
    retryRef.current = 0;
    setLoaded(false);
    setSrc(getAssetUrl(path, variant));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [path, variant]);

  // 마운트 시 이미 캐시된 이미지면 onLoad가 안 터지므로 직접 확인 (리마운트 후 검정 방지)
  const imgRef = useCallback((el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, []);

  const handleError = () => {
    const attempt = retryRef.current;
    if (attempt < RETRY_DELAYS_MS.length) {
      // 변형이 아직 생성 안 됨 — 잠깐 후 cache-bust 재요청
      retryRef.current = attempt + 1;
      timerRef.current = setTimeout(() => {
        setSrc(`${getAssetUrl(path, variant)}?r=${attempt + 1}`);
      }, RETRY_DELAYS_MS[attempt]);
    } else {
      // 재시도 소진 — 업로드 직후 즉시 존재하는 원본으로 폴백
      const original = getAssetUrl(path, "original");
      if (src !== original) setSrc(original);
    }
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: 2,
        overflow: "hidden",
        maxHeight,
        bgcolor: c.bgInset,
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt=""
        onLoad={() => setLoaded(true)}
        onError={handleError}
        style={{
          display: "block",
          width: "100%",
          objectFit: "cover",
          maxHeight,
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      />
    </Box>
  );
}
